// Single resolution timeline node — icon, status, agent label, collapsible results
import { useState } from 'react'
import { ChevronDown, ChevronRight, Loader2, CheckCircle, SkipForward, AlertCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ResolutionTierResult } from '@/types/issue'

interface ResolutionTimelineNodeProps {
  tier: ResolutionTierResult
  label: string
  agentLabel: string
  icon: React.ReactNode
  isLast: boolean
  isPaused?: boolean
  isAccepted?: boolean
  children?: React.ReactNode
}

const STATUS_INDICATOR: Record<string, JSX.Element> = {
  pending: <span className="h-3 w-3 rounded-full border-2 border-gray-300" />,
  running: <Loader2 className="h-3 w-3 animate-spin text-kaycoo-teal-dark" />,
  complete: <CheckCircle className="h-3 w-3 text-emerald-500" />,
  skipped: <SkipForward className="h-3 w-3 text-gray-400" />,
  error: <AlertCircle className="h-3 w-3 text-red-500" />,
}

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-blue-50 text-blue-700 border-blue-200',
  low: 'bg-amber-50 text-amber-700 border-amber-200',
  none: 'bg-gray-50 text-gray-500 border-gray-200',
}

function confidenceLevel(score: number): string {
  if (score >= 0.85) return 'high'
  if (score >= 0.70) return 'medium'
  if (score > 0) return 'low'
  return 'none'
}

export function ResolutionTimelineNode({
  tier, label, agentLabel, icon, isLast, isPaused, isAccepted, children,
}: ResolutionTimelineNodeProps): JSX.Element {
  const [expanded, setExpanded] = useState(
    tier.status === 'complete' || tier.status === 'running' || isPaused === true
  )
  const hasContent = tier.status === 'complete' || tier.status === 'error'
  const resultCount = tier.results?.length ?? 0

  return (
    <div className="flex gap-3">
      {/* Vertical line + dot */}
      <div className="flex flex-col items-center">
        <div className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white',
          isPaused ? 'border-kaycoo-teal ring-2 ring-kaycoo-teal/30' :
          isAccepted ? 'border-emerald-500 bg-emerald-50' :
          'border-gray-200',
        )}>
          {isAccepted
            ? <CheckCircle className="h-3 w-3 text-emerald-600" />
            : (STATUS_INDICATOR[tier.status] ?? STATUS_INDICATOR.pending)}
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-gray-200" />}
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <button
          className="flex w-full items-center gap-2 text-left"
          onClick={() => hasContent && setExpanded(!expanded)}
          disabled={!hasContent}
        >
          <span className="text-kaycoo-charcoal-2">{icon}</span>
          <div className="flex flex-col">
            <span className={cn(
              'text-sm font-medium',
              tier.status === 'skipped' ? 'text-gray-400' : 'text-kaycoo-charcoal',
            )}>
              {label}
            </span>
            <span className="text-[10px] text-kaycoo-charcoal-2">{agentLabel}</span>
          </div>

          {tier.status === 'complete' && tier.bestConfidence > 0 && (
            <Badge variant="outline" className={cn('ml-1 text-xs', CONFIDENCE_COLORS[confidenceLevel(tier.bestConfidence)])}>
              {(tier.bestConfidence * 100).toFixed(0)}%
            </Badge>
          )}

          {tier.status === 'complete' && resultCount > 0 && (
            <span className="text-xs text-gray-400">{resultCount} result{resultCount !== 1 ? 's' : ''}</span>
          )}

          {tier.expertName && (
            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
              {tier.expertName}
            </Badge>
          )}

          {isAccepted && <Badge className="ml-1 bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Accepted</Badge>}
          {tier.status === 'skipped' && <span className="text-xs text-gray-400">Skipped</span>}
          {tier.status === 'running' && <span className="text-xs text-kaycoo-teal-dark">Searching...</span>}

          {hasContent && (
            expanded ? <ChevronDown className="ml-auto h-4 w-4 text-gray-400" />
                     : <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
          )}
        </button>

        {expanded && tier.status === 'error' && (
          <p className="mt-2 text-sm text-red-600">{tier.error || 'Search failed'}</p>
        )}

        {expanded && tier.status === 'complete' && children}
      </div>
    </div>
  )
}
